import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import DeleteModal from '../../../../components/modals/DeleteModal';
import { permanentlyDeleteUser } from '../../api/usersApi';

// Posle uspeha modal kratko prikazuje "done" (kvacica), pa se zatvara - isto kao
// postojeca trajna deaktivacija.
const CLOSE_DELAY_MS = 1000;

const deletedDataItems = [
  'terapije i termini',
  'zahtevi i rezervacije',
  'istorija aktivnosti i istorija notifikacija',
  'chat razgovor sa svim porukama',
  'slike saglasnosti',
];

// Trajno brisanje pacijenta (DELETE /users/:id/permanent). Za razliku od
// "Trajno deaktiviraj nalog" (soft delete, status "deleted"), ovde se pacijent i
// svi njegovi podaci nepovratno brisu. Modal drzi sopstveno stanje (lozinka,
// greska, loading) i roditelju javlja samo ishod.
const PermanentDeleteUserModal = ({
  isOpen,
  user,
  userDisplayName,
  onClose,
  onDeleted,
  onNotFound,
}) => {
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    // Zatvoren rucno pre isteka - automatsko zatvaranje vise nije potrebno.
    clearTimeout(closeTimerRef.current);
    if (!isOpen) return;
    setPassword('');
    setIsPasswordVisible(false);
    setError('');
    setLoading(false);
    setDone(false);
  }, [isOpen, user?._id]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleConfirm = async () => {
    if (!user?._id || loading || done) return;
    if (!password.trim()) {
      setError('Unesite svoj PIN.');
      return;
    }

    setLoading(true);
    setError('');
    const { ok, message, status, code } = await permanentlyDeleteUser(
      user._id,
      password.trim()
    );
    setLoading(false);

    // Svaka greska (pogresna lozinka, nije pacijent...) ostavlja dijalog otvoren
    // sa porukom sa beka, da admin moze da pokusa ponovo ili odustane.
    if (!ok) {
      setError(message || 'Doslo je do greske pri trajnom brisanju pacijenta.');
      // Pacijent vise ne postoji (npr. obrisan u drugom tabu) - osvezi listu da
      // red nestane.
      if (status === 404 && code === 'USER_NOT_FOUND') onNotFound?.();
      return;
    }

    setDone(true);
    onDeleted(user._id, message);
    closeTimerRef.current = setTimeout(onClose, CLOSE_DELAY_MS);
  };

  return (
    <DeleteModal
      className="delete_user_modal"
      visible={isOpen}
      onDelete={handleConfirm}
      onCancel={onClose}
      loading={loading}
      done={done}
      title="Trajno brisanje pacijenta"
      message={`Pacijent ${userDisplayName} i svi njegovi podaci biće nepovratno obrisani.`}
      buttonText="Trajno obriši"
      confirmDisabled={!password.trim()}
    >
      <Alert
        type="error"
        showIcon
        title="Ova radnja se ne može poništiti."
        description={
          <>
            <div>Brišu se:</div>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              {deletedDataItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div>Kontakt (lekar) ostaje.</div>
            <div style={{ marginTop: 6 }}>
              Ako nalog treba samo onemogućiti, koristite „Trajno deaktiviraj
              nalog”.
            </div>
          </>
        }
      />
      <p style={{ margin: '12px 0 0' }}>
        Unesite svoj PIN da potvrdite brisanje.
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Input
          type={isPasswordVisible ? 'text' : 'password'}
          placeholder="Vas PIN"
          inputMode="numeric"
          value={password}
          disabled={done}
          onChange={(event) => {
            setPassword(event.target.value);
            if (error) setError('');
          }}
          onPressEnter={handleConfirm}
        />
        <Button
          type="default"
          onClick={() => setIsPasswordVisible((visible) => !visible)}
          aria-label={isPasswordVisible ? 'Sakrij PIN' : 'Prikazi PIN'}
        >
          {isPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </Button>
      </div>
      {!!error && (
        <Alert title={error} type="error" showIcon style={{ marginTop: 12 }} />
      )}
    </DeleteModal>
  );
};

PermanentDeleteUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  user: PropTypes.object,
  userDisplayName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
  onNotFound: PropTypes.func,
};

export default PermanentDeleteUserModal;
